/**
 * CRM Multi-Compagnia · Lista Polizze + drawer dettaglio con tab + form
 */
import { useState, useMemo } from "react";
import { FileText, Plus, Edit3, Trash2, Sparkles } from "lucide-react";
import {
  api, useApi, useDebounced, DataTable, Spinner, ErrorBox, Btn, Drawer, Modal,
  Field, TextInput, Select, RamoChip, StatusBadge, CompanyAvatar, toast,
  fmtDate, fmtEUR, RAMI_LABEL
} from "@/components/crm-multi/shared";

const RAMI_LIST = Object.keys(RAMI_LABEL);
const STATI = ["bozza", "attiva", "sospesa", "scaduta", "disdetta", "annullata"];
const FRAZ = ["annuale", "semestrale", "quadrimestrale", "trimestrale", "mensile", "unica"];

const EMPTY = {
  numero_polizza: "", customer_id: "", company_id: "", product_id: "",
  ramo: "casa", stato: "bozza", data_decorrenza: "", data_scadenza: "",
  durata_anni: 1, frazionamento: "annuale", premio_lordo_annuo: 0, tacito_rinnovo: true,
};

export default function CrmMultiPolizzePage() {
  const [filters, setFilters] = useState({ stato: "", ramo: "", company_id: "", in_scadenza_giorni: "" });
  const [q, setQ] = useState("");
  const [quickFilter, setQuickFilter] = useState("all");
  const dq = useDebounced(q);

  const params = useMemo(() => {
    const p = { limit: 200, q: dq || undefined };
    Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    if (quickFilter === "scadenza_30") p.in_scadenza_giorni = 30;
    if (quickFilter === "bozze") p.stato = "bozza";
    return p;
  }, [filters, dq, quickFilter]);

  const list = useApi(() => api.get("/policies", params), [params]);
  const compagnie = useApi(() => api.get("/companies", { limit: 200 }));
  const prodotti = useApi(() => api.get("/products", { limit: 500 }));
  const clienti = useApi(() => api.get("/customers", { limit: 500 }));
  const [drawerId, setDrawerId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [promoteOpen, setPromoteOpen] = useState(false);

  const polizzeConInsoluti = useMemo(() => new Set((unpaid.data?.items || []).map(u => u.policy_id)), [unpaid.data]);
  const items = (list.data?.items || []).filter(p => quickFilter !== "insolute" || polizzeConInsoluti.has(p.id));
  const selected = items.find(p => p.id === drawerId);
  const compMap = Object.fromEntries((compagnie.data?.items || []).map(c => [c.id, c.nome]));
  const cliMap = Object.fromEntries((clienti.data?.items || []).map(c => [c.id, c]));

  const onSave = async (form) => {
    try {
      if (editing.mode === "new") await api.post("/policies", form);
      else await api.patch(`/policies/${editing.data.id}`, form);
      toast("Polizza salvata", "success"); setEditing(null); list.refresh();
    } catch (e) { toast(JSON.stringify(e?.response?.data?.detail || e.message), "error"); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Eliminare la polizza?")) return;
    try { await api.del(`/policies/${id}`); toast("Eliminata", "success"); list.refresh(); setDrawerId(null); }
    catch (e) { toast(e.message, "error"); }
  };

  return (
    <div className="space-y-4" data-testid="cm-polizze-page">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-zinc-900 flex items-center gap-2"><FileText className="w-5 h-5" /> Polizze</h2>
          <p className="text-sm text-zinc-500">{list.data?.total ?? 0} polizze totali</p>
        </div>
        <div className="flex gap-2">
          <Btn variant="outline" onClick={() => setPromoteOpen(true)} data-testid="cm-polizze-promote"><Sparkles className="w-4 h-4" /> Promuovi lead</Btn>
          <Btn variant="primary" onClick={() => setEditing({ mode: "new", data: EMPTY })} data-testid="cm-polizze-new"><Plus className="w-4 h-4" /> Nuova polizza</Btn>
        </div>
      </header>

      {/* Quick filters */}
      <div className="flex gap-2 flex-wrap">
        {[["all","Tutte"],["scadenza_30","Scadenza 30gg"],["insolute",`Insolute (${polizzeConInsoluti.size})`],["bozze","Bozze"]].map(([k,l]) => (
          <button key={k} onClick={() => setQuickFilter(k)}
            data-testid={`cm-polizze-quick-${k}`}
            className={`px-3 py-1 rounded-full text-xs font-medium ${quickFilter === k ? "bg-emerald-600 text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-4 gap-2">
        <TextInput placeholder="Cerca per nr. polizza..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="cm-polizze-search" />
        <Select value={filters.company_id} onChange={(e) => setFilters({ ...filters, company_id: e.target.value })}>
          <option value="">Tutte le compagnie</option>
          {(compagnie.data?.items || []).map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
        </Select>
        <Select value={filters.ramo} onChange={(e) => setFilters({ ...filters, ramo: e.target.value })}>
          <option value="">Tutti i rami</option>
          {RAMI_LIST.map(r => <option key={r} value={r}>{RAMI_LABEL[r]}</option>)}
        </Select>
        <Select value={filters.stato} onChange={(e) => setFilters({ ...filters, stato: e.target.value })}>
          <option value="">Tutti gli stati</option>
          {STATI.map(s => <option key={s} value={s}>{s}</option>)}
        </Select>
      </div>

      <ErrorBox error={list.error} />
      {list.loading ? <Spinner /> : (
        <DataTable
          testid="cm-polizze-table"
          onRowClick={(r) => setDrawerId(r.id)}
          columns={[
            { key: "numero_polizza", label: "Nr. Polizza", render: r => <span className="font-mono text-xs">{r.numero_polizza}</span> },
            { key: "compagnia", label: "Compagnia", render: r => <div className="flex items-center gap-2"><CompanyAvatar nome={compMap[r.company_id]} size="sm" /><span className="text-xs">{compMap[r.company_id] || "?"}</span></div> },
            { key: "cliente", label: "Cliente", render: r => {
              const c = cliMap[r.customer_id]?.anagrafica;
              if (!c) return <span className="text-zinc-400">—</span>;
              return <span className="text-xs">{c.tipo === "azienda" ? c.ragione_sociale : `${c.nome || ""} ${c.cognome || ""}`}</span>;
            }},
            { key: "ramo", label: "Ramo", render: r => <RamoChip ramo={r.ramo} /> },
            { key: "data_scadenza", label: "Scadenza", render: r => <span className="text-xs">{fmtDate(r.data_scadenza)}</span> },
            { key: "premio", label: "Premio", render: r => <span className="text-xs font-mono">{fmtEUR(r.premio_lordo_annuo)}</span> },
            { key: "stato", label: "Stato", render: r => <StatusBadge tipo="polizza" valore={r.stato} /> },
          ]}
          rows={items}
          emptyTitle="Nessuna polizza"
          emptyHint="Crea una nuova polizza o promuovi un lead dalle richieste."
        />
      )}

      <Drawer
        open={!!selected} onClose={() => setDrawerId(null)}
        title={`Polizza ${selected?.numero_polizza}`}
        subtitle={`${RAMI_LABEL[selected?.ramo]} · ${compMap[selected?.company_id]}`}
        actions={selected && (
          <>
            <Btn size="sm" variant="outline" onClick={() => setEditing({ mode: "edit", data: selected })}><Edit3 className="w-3.5 h-3.5" /></Btn>
            <Btn size="sm" variant="danger" onClick={() => onDelete(selected.id)}><Trash2 className="w-3.5 h-3.5" /></Btn>
          </>
        )}
        testid="cm-polizze-drawer"
      >
        {selected && <PolizzaDetailTabs policy={selected} compMap={compMap} cliMap={cliMap} />}
      </Drawer>

      {editing && <PolizzaEditModal data={editing.data} mode={editing.mode} onSave={onSave} onClose={() => setEditing(null)} compagnie={compagnie.data?.items || []} prodotti={prodotti.data?.items || []} clienti={clienti.data?.items || []} />}
      {promoteOpen && <PromoteLeadModal onClose={() => setPromoteOpen(false)} onDone={() => { setPromoteOpen(false); list.refresh(); }} compagnie={compagnie.data?.items || []} prodotti={prodotti.data?.items || []} />}
    </div>
  );
}

function PolizzaDetailTabs({ policy, compMap, cliMap }) {
  const [tab, setTab] = useState("dati");
  const garanzie = useApi(() => api.get("/guarantees", { policy_id: policy.id }), [policy.id]);
  const rate = useApi(() => api.get("/installments", { policy_id: policy.id, limit: 50 }), [policy.id]);
  const incassi = useApi(() => api.get("/payments", { policy_id: policy.id }), [policy.id]);
  const sinistri = useApi(() => api.get("/claims", { customer_id: policy.customer_id }), [policy.customer_id]);

  return (
    <div>
      <nav className="flex gap-1 border-b border-zinc-200 mb-4 flex-wrap">
        {[["dati","Dati"],["garanzie",`Garanzie (${garanzie.data?.total ?? "..."})`],["rate",`Rate (${rate.data?.total ?? "..."})`],["incassi",`Incassi (${incassi.data?.total ?? "..."})`],["sinistri","Sinistri"]].map(([id,l]) => (
          <button key={id} onClick={() => setTab(id)} className={`px-3 py-2 text-sm border-b-2 -mb-px ${tab === id ? "border-emerald-600 text-emerald-700" : "border-transparent text-zinc-500"}`}>{l}</button>
        ))}
      </nav>
      {tab === "dati" && (
        <dl className="grid grid-cols-2 gap-y-3 text-sm">
          <KV k="Numero polizza" v={<code>{policy.numero_polizza}</code>} />
          <KV k="Stato" v={<StatusBadge tipo="polizza" valore={policy.stato} />} />
          <KV k="Compagnia" v={compMap[policy.company_id]} />
          <KV k="Cliente" v={(() => { const c = cliMap[policy.customer_id]?.anagrafica; return c ? (c.tipo === "azienda" ? c.ragione_sociale : `${c.nome} ${c.cognome}`) : "—"; })()} />
          <KV k="Ramo" v={<RamoChip ramo={policy.ramo} />} />
          <KV k="Frazionamento" v={policy.frazionamento} />
          <KV k="Decorrenza" v={fmtDate(policy.data_decorrenza)} />
          <KV k="Scadenza" v={fmtDate(policy.data_scadenza)} />
          <KV k="Premio annuo" v={fmtEUR(policy.premio_lordo_annuo)} />
          <KV k="Premio rata" v={fmtEUR(policy.premio_rata)} />
          <KV k="Tacito rinnovo" v={policy.tacito_rinnovo ? "Sì" : "No"} />
          <KV k="External SIA ID" v={<code className="text-xs">{policy.external_sia_policy_id || "—"}</code>} />
          {policy.source_richiesta_id && <div className="col-span-2"><KV k="Lead di origine" v={<code className="text-xs">{policy.source_richiesta_id}</code>} /></div>}
        </dl>
      )}
      {tab === "garanzie" && ((garanzie.data?.items || []).length === 0 ? <p className="text-sm text-zinc-500">Nessuna garanzia.</p> :
        garanzie.data.items.map(g => (
          <div key={g.id} className="border rounded p-2 mb-2 text-sm">
            <p className="font-medium">{g.nome} <code className="text-[10px] text-zinc-400">{g.codice}</code></p>
            <p className="text-xs text-zinc-500">Massimale: {fmtEUR(g.massimale_eur)} · Franchigia: {fmtEUR(g.franchigia_eur)} · Premio: {fmtEUR(g.premio_eur)}</p>
          </div>
        )))}
      {tab === "rate" && ((rate.data?.items || []).length === 0 ? <p className="text-sm text-zinc-500">Nessuna rata.</p> :
        rate.data.items.map(r => (
          <div key={r.id} className="border rounded p-2 mb-2 text-sm flex items-center justify-between">
            <span>Rata #{r.numero_rata} — {fmtDate(r.data_scadenza)}</span>
            <span className="flex items-center gap-2"><span className="font-mono text-xs">{fmtEUR(r.importo_eur)}</span><StatusBadge tipo="incasso" valore={r.stato} /></span>
          </div>
        )))}
      {tab === "incassi" && ((incassi.data?.items || []).length === 0 ? <p className="text-sm text-zinc-500">Nessun incasso.</p> :
        incassi.data.items.map(p => (
          <div key={p.id} className="border rounded p-2 mb-2 text-sm flex items-center justify-between">
            <span>{fmtDate(p.data_incasso)} · {p.metodo}</span>
            <span className="font-mono text-xs">{fmtEUR(p.importo_eur)}</span>
          </div>
        )))}
      {tab === "sinistri" && ((sinistri.data?.items || []).length === 0 ? <p className="text-sm text-zinc-500">Nessun sinistro per questo cliente.</p> :
        sinistri.data.items.map(s => (
          <div key={s.id} className="border rounded p-2 mb-2 text-sm">
            <span className="font-mono text-xs">{s.numero_sinistro}</span> · {fmtDate(s.data_sinistro)} · <StatusBadge tipo="sinistro" valore={s.stato} />
            <p className="text-xs text-zinc-500">{s.descrizione}</p>
          </div>
        )))}
    </div>
  );
}

const KV = ({ k, v }) => <div><dt className="text-[10px] uppercase font-medium text-zinc-500 tracking-wider">{k}</dt><dd className="text-sm text-zinc-900 mt-0.5">{v || "—"}</dd></div>;

function PolizzaEditModal({ data, mode, onSave, onClose, compagnie, prodotti, clienti }) {
  const [form, setForm] = useState(data);
  const prodottiFiltrati = prodotti.filter(p => p.company_id === form.company_id);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="cm-polizza-edit">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <header className="px-5 py-3 border-b flex justify-between"><h3 className="text-sm font-semibold">{mode === "new" ? "Nuova polizza" : "Modifica polizza"}</h3><button onClick={onClose} className="text-xl">×</button></header>
        <form onSubmit={(e) => { e.preventDefault(); onSave(form); }} className="p-5 grid grid-cols-2 gap-3">
          <Field label="Numero polizza" required><TextInput required value={form.numero_polizza} onChange={(e) => setForm({ ...form, numero_polizza: e.target.value })} /></Field>
          <Field label="Stato"><Select value={form.stato} onChange={(e) => setForm({ ...form, stato: e.target.value })}>{STATI.map(s => <option key={s}>{s}</option>)}</Select></Field>
          <Field label="Cliente" required>
            <Select required value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })}>
              <option value="">Seleziona...</option>
              {clienti.map(c => <option key={c.id} value={c.id}>{c.anagrafica?.tipo === "azienda" ? c.anagrafica.ragione_sociale : `${c.anagrafica?.nome} ${c.anagrafica?.cognome}`}</option>)}
            </Select>
          </Field>
          <Field label="Ramo" required>
            <Select required value={form.ramo} onChange={(e) => setForm({ ...form, ramo: e.target.value })}>
              {RAMI_LIST.map(r => <option key={r} value={r}>{RAMI_LABEL[r]}</option>)}
            </Select>
          </Field>
          <Field label="Compagnia" required>
            <Select required value={form.company_id} onChange={(e) => setForm({ ...form, company_id: e.target.value, product_id: "" })}>
              <option value="">Seleziona...</option>
              {compagnie.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </Select>
          </Field>
          <Field label="Prodotto" required>
            <Select required value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })}>
              <option value="">Seleziona...</option>
              {prodottiFiltrati.map(p => <option key={p.id} value={p.id}>{p.nome}</option>)}
            </Select>
          </Field>
          <Field label="Data decorrenza"><TextInput type="date" value={form.data_decorrenza || ""} onChange={(e) => setForm({ ...form, data_decorrenza: e.target.value })} /></Field>
          <Field label="Data scadenza"><TextInput type="date" value={form.data_scadenza || ""} onChange={(e) => setForm({ ...form, data_scadenza: e.target.value })} /></Field>
          <Field label="Frazionamento"><Select value={form.frazionamento} onChange={(e) => setForm({ ...form, frazionamento: e.target.value })}>{FRAZ.map(f => <option key={f}>{f}</option>)}</Select></Field>
          <Field label="Premio lordo annuo €"><TextInput type="number" step="0.01" value={form.premio_lordo_annuo || 0} onChange={(e) => setForm({ ...form, premio_lordo_annuo: parseFloat(e.target.value || 0) })} /></Field>
          <div className="col-span-2 flex justify-end gap-2 pt-3 border-t"><Btn variant="outline" type="button" onClick={onClose}>Annulla</Btn><Btn variant="primary" type="submit" data-testid="cm-polizza-form-save">Salva</Btn></div>
        </form>
      </div>
    </div>
  );
}

function PromoteLeadModal({ onClose, onDone, compagnie, prodotti }) {
  // Carica TUTTE le richieste pubbliche e poi filtra quelle già promosse
  const richieste = useApi(() => fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/quote-submissions`, { credentials: "include" }).then(r => r.ok ? r.json() : { submissions: [] }));
  const polizzePromosse = useApi(() => api.get("/policies", { limit: 500 }));
  const promosseIds = useMemo(() => new Set((polizzePromosse.data?.items || []).map(p => p.source_richiesta_id).filter(Boolean)), [polizzePromosse.data]);
  const nonPromosse = ((richieste.data?.submissions || richieste.data?.items || [])).filter(r => !promosseIds.has(r.id));

  const [selected, setSelected] = useState(null);
  const [companyId, setCompanyId] = useState("");
  const [productId, setProductId] = useState("");
  const [ramo, setRamo] = useState("casa");
  const [premio, setPremio] = useState(0);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const r = await api.post(`/policies/from-richiesta/${selected.id}`, { company_id: companyId, product_id: productId, ramo, premio_lordo_annuo: premio });
      toast(r.already_promoted ? "Lead già promosso (idempotente)" : "Lead promosso a polizza!", "success");
      onDone();
    } catch (e2) { toast(JSON.stringify(e2?.response?.data?.detail || e2.message), "error"); }
    finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title="Promuovi lead → polizza" maxWidth="max-w-3xl" testid="cm-promote-modal">
      {!selected ? (
        <div>
          <p className="text-sm text-zinc-600 mb-3">
            <strong>{nonPromosse.length}</strong> richieste non ancora promosse. Clicca per promuoverla.
          </p>
          <div className="max-h-96 overflow-y-auto border rounded">
            {richieste.loading ? <Spinner /> : nonPromosse.length === 0 ? (
              <p className="text-sm text-zinc-500 text-center py-8">Nessuna richiesta da promuovere.</p>
            ) : nonPromosse.map(r => (
              <button key={r.id} onClick={() => setSelected(r)}
                className="w-full text-left p-3 border-b hover:bg-emerald-50 text-sm"
                data-testid={`cm-promote-row-${r.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{r.contact?.nome} {r.contact?.cognome} {r.contact?.ragione_sociale}</p>
                    <p className="text-xs text-zinc-500">{r.type} · {r.contact?.email} · {fmtDate(r.created_at)}</p>
                  </div>
                  <code className="text-[10px] text-zinc-400">{r.id?.slice(0, 8)}</code>
                </div>
              </button>
            ))}
          </div>
          <div className="flex justify-end pt-3"><Btn variant="outline" type="button" onClick={onClose}>Chiudi</Btn></div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-sm">
            <strong>Lead:</strong> {selected.contact?.nome} {selected.contact?.cognome} · <code className="text-xs">{selected.id}</code>
            <button type="button" onClick={() => setSelected(null)} className="ml-2 text-xs text-emerald-700 underline">Cambia</button>
          </div>
          <Field label="Compagnia" required>
            <Select required value={companyId} onChange={(e) => { setCompanyId(e.target.value); setProductId(""); }}>
              <option value="">Seleziona...</option>{compagnie.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </Select>
          </Field>
          <Field label="Prodotto" required>
            <Select required value={productId} onChange={(e) => setProductId(e.target.value)}>
              <option value="">Seleziona...</option>
              {prodotti.filter(p => p.company_id === companyId).map(p => <option key={p.id} value={p.id}>{p.nome}</option>)}
            </Select>
          </Field>
          <Field label="Ramo"><Select value={ramo} onChange={(e) => setRamo(e.target.value)}>{RAMI_LIST.map(r => <option key={r} value={r}>{RAMI_LABEL[r]}</option>)}</Select></Field>
          <Field label="Premio annuo stimato €"><TextInput type="number" value={premio} onChange={(e) => setPremio(parseFloat(e.target.value || 0))} /></Field>
          <div className="flex justify-end gap-2">
            <Btn variant="outline" type="button" onClick={() => setSelected(null)}>Indietro</Btn>
            <Btn variant="primary" type="submit" disabled={busy} data-testid="cm-promote-submit">Promuovi</Btn>
          </div>
        </form>
      )}
    </Modal>
  );
}
